import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";
import { CERTIFICATIONS, CONTACT_INFO } from "@/data/content";

export default function Certifications() {
  return (
    <div data-testid="certifications-page" className="pt-[72px]">
      {/* Header */}
      <section data-testid="certifications-header" className="relative overflow-hidden grain border-b border-white/10">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 md:py-28 relative z-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-6">
            Credentials & Certifications
          </p>
          <h1 className="font-heading font-black uppercase tracking-tighter text-5xl sm:text-6xl md:text-7xl leading-[0.95] max-w-4xl">
            <MaskLine delay={0.1}>Cleared for</MaskLine>
            <MaskLine delay={0.22}>
              the <span className="text-blue-500">mission.</span>
            </MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="mt-8 max-w-2xl text-lg text-white/60 font-light leading-relaxed"
          >
            Every certification below is active and verifiable — the foundation for secure,
            compliant delivery to Federal agencies and the Department of Defense.
          </motion.p>
        </div>
      </section>

      {/* Certification bento grid */}
      <section data-testid="certifications-grid" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 md:py-28">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
          {CERTIFICATIONS.map((c, i) => (
            <Reveal
              key={c.title}
              delay={(i % 3) * 0.08}
              className="group relative p-8 md:p-10 border-r border-b border-white/10 bg-[#141414] hover:bg-[#171717] transition-colors"
              data-testid={`cert-card-${i}`}
            >
              <div className="absolute inset-0 border border-transparent group-hover:border-blue-500/50 transition-colors pointer-events-none" />
              <span className="inline-block font-mono text-xs font-semibold uppercase tracking-[0.2em] text-blue-400 border border-blue-500/30 px-3 py-1 mb-6">
                {c.code}
              </span>
              <h3 className="font-heading font-bold text-xl md:text-2xl tracking-tight mb-3">{c.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed font-light">{c.desc}</p>
            </Reveal>
          ))}
          {/* Bonded highlight cell */}
          <Reveal className="relative p-8 md:p-10 border-r border-b border-white/10 bg-[#0055FF] flex flex-col justify-between min-h-[220px]">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/80">Bonded & Insured</p>
            <p className="font-heading font-black text-2xl md:text-3xl tracking-tight leading-tight text-white">
              Fully bonded for large-scale federal contracts.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Registrations */}
      <section data-testid="registrations-section" className="relative bg-[#0A0A0A] border-y border-white/10 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal className="mb-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-4">Federal Registrations</p>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">
              Registered and ready to contract.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
            {[
              { label: "CAGE Code", value: CONTACT_INFO.cage },
              { label: "Unique Entity ID (UEI)", value: CONTACT_INFO.uei },
              { label: "DUNS", value: CONTACT_INFO.duns },
              { label: "GSA Contract No.", value: CONTACT_INFO.gsa },
            ].map((r) => (
              <div
                key={r.label}
                data-testid={`registration-${r.label}`}
                className="p-8 border-r border-b border-white/10 bg-[#141414]"
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 mb-3">{r.label}</p>
                <p className="font-heading font-bold text-2xl md:text-3xl tracking-tight text-white">{r.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="certs-cta" className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 text-center">
          <Reveal>
            <h2 className="font-heading font-black uppercase tracking-tighter text-3xl sm:text-4xl md:text-5xl leading-[0.95] max-w-3xl mx-auto">
              Need a compliant, secure build? <span className="text-blue-500">Let&apos;s talk.</span>
            </h2>
            <Link
              to="/contact"
              data-testid="certs-cta-button"
              className="inline-flex items-center gap-2 mt-8 px-8 py-4 bg-[#0055FF] hover:bg-[#0044CC] text-white font-bold uppercase tracking-wide text-sm transition-transform hover:scale-[1.04]"
            >
              Start a Project <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
